import React, { useState, useEffect, FC, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Form, Button, Modal, Row, Divider, message, Table, Popconfirm, Input } from 'antd'
import { CompareFn, FilterDropdownProps } from 'antd/lib/table'

import { License, Consumer, RequestErrorObject, RequestErrorInterface, ValidateConsumerUploadResponse, ConsumerUploadRecord } from '../../../types'
import { consumersService, licensesService } from '../../../services'
import { handleError } from '../../../components'
import AssignUser from './AssignUser'

import styles from './index.module.less'
import TotalLicenses from './TotalLicenses'
import ValidateUpload from './BulkUploadConsumers/ValidateUpload'
import FileSelector from './BulkUploadConsumers/FileSelector'

interface EditLicenseModalProps {
	customerId: number
	license: License
	onConsumersUpdated(serial: string, quantity: number): void
	onLicenseUpdated(serial: string, license: License): void
	isAdmin: boolean
}

const EditLicenseModal: FC<EditLicenseModalProps> = ({ customerId, license, onConsumersUpdated, onLicenseUpdated, isAdmin }) => {
	const [consumers, setConsumers] = useState<Consumer[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [addValue, setAddValue] = useState<string>('')
	const [uploadData, setUploadData] = useState<ValidateConsumerUploadResponse>(null)

	const history = useHistory()
	const location = useLocation()
	const queryParams = new URLSearchParams(location.search)
	const usernameInQuery = queryParams.get('username')
	const licenseTypeInQuery = queryParams.get('type')
	const licenseSerialInQuery = queryParams.get('serial')
	const validLicenseInQuery = licenseTypeInQuery?.toUpperCase() === license?.type?.toUpperCase() && licenseSerialInQuery === license.serial

	const assign = async (userId: string): Promise<void> => {
		const createAssignLicenseError = (error: RequestErrorInterface): RequestErrorObject => {
			const source = 'Assign License Error'
			const base = { source, status: error.response.status }

			switch (error.response.status) {
				case 422:
					return {
						...base,
						description: 'Unable to add subscriber. Potentially a duplicate Subscriber ID or you have hit the limit of consumers.'
					}
				default:
					return { ...base, description: 'Unable to add subscriber. Please try again later or report this.' }
			}
		}

		try {
			if (!userId) {
				throw new Error('Please enter a Subscriber ID')
			}
			setIsLoading(true)
			const consumer = await consumersService.add(customerId, license.serial, userId)
			setConsumers(prev => {
				const newConsumers = [...prev, consumer.data]
				return newConsumers
			})
		} catch (error) {
			handleError(error as Error, createAssignLicenseError)
		} finally {
			setIsLoading(false)
			setAddValue('')
		}
	}

	const unassign = async (userId: string): Promise<void> => {
		const createUnassignLicenseError = (error: RequestErrorInterface): RequestErrorObject => {
			const source = 'Unassign License Error'
			const base = { source, status: error.response.status }

			switch (error.response.status) {
				case 404:
					return { ...base, description: 'Could not find subscriber. This information may be outdated.' }
				default:
					return { ...base, description: 'Unable to remove subscriber. Please try again later or report this.' }
			}
		}

		try {
			setIsLoading(true)
			await consumersService.remove(customerId, license.serial, userId)
			setConsumers(prev => {
				const newConsumers = prev.filter(consumer => consumer.username !== userId)
				return newConsumers
			})
		} catch (error) {
			handleError(error as Error, createUnassignLicenseError)
		} finally {
			setIsLoading(false)
		}
	}

	const createLicenseUpdateError = (error: RequestErrorInterface): RequestErrorObject | RequestErrorObject[] => {
		const source = 'License Update'
		const base = { status: error.response.status, source }

		switch (error.response.status) {
			case 500:
				return { ...base, description: 'Error updating license, experienced server error.' }
			case 422:
				return Object.keys(error.response.body).flatMap(prop => {
					const errors = (error.response.body as Record<string, string[]>)[prop]
					return errors.map(e => ({ ...base, description: `Error updating license. ${e}` }))
				})
			default:
				return { ...base, description: 'Error updating license' }
		}
	}

	const updateTotal = async (total: number) => {
		try {
			const { data } = await licensesService.update(customerId, license.serial, total)
			onLicenseUpdated(license.serial, data)
			message.success('License updated')
		} catch (error) {
			handleError(error as Error, createLicenseUpdateError)
		}
	}

	const validateFile = async (file: File) => {
		try {
			const response = await consumersService.validate(customerId, license?.serial, file)
			setUploadData(response)
		} catch (error) {
			handleError(error as Error, () => {
				const status = (error as RequestErrorInterface).response?.status
				return {
					source: 'Validate consumer upload',
					status,
					description:
						status === 422
							? 'This file is incorrectly formatted. Did you use the provided template?'
							: 'Something went wrong when attempting to validate this file. Please try again.'
				}
			})
		}
	}

	const uploadConsumerData = async () => {
		const body: ConsumerUploadRecord[] = uploadData.passed.map(({ username, action }) => ({ username, action }))
		try {
			await consumersService.upload(customerId, license?.serial, body)
			message.success('Licenses updated')
			await getConsumers()
		} catch (error) {
			handleError(error as Error, () => {
				const status = (error as RequestErrorInterface).response?.status
				return {
					source: 'Validate consumer upload',
					status,
					description: 'Something went wrong when attempting to upload this file. Please try again.'
				}
			})
		} finally {
			setUploadData(null)
		}
	}

	const getConsumers = useCallback(async () => {
		const createConsumersErrorObject = (error: RequestErrorInterface): RequestErrorObject => ({
			source: 'Get Consumers',
			status: error.response.status,
			description: 'Could not get consumers. Please try again or report this.'
		})

		try {
			setIsLoading(true)
			setConsumers([])
			const { data } = await consumersService.get(customerId, license.serial)
			setConsumers(data)
		} catch (error) {
			handleError(error as Error, createConsumersErrorObject)
		} finally {
			setIsLoading(false)
		}
	}, [license.serial, customerId])

	const close = () => {
		onConsumersUpdated(license.serial, consumers.length)
		history.push(location.pathname)
	}

	const sortByUsername: CompareFn<Consumer> = (a, b) => {
		return a.username.localeCompare(b.username)
	}

	const filterByUsername = (value: string, record: Consumer) => record.username.toLowerCase().includes(value.toLowerCase())

	const Filter: React.VFC<FilterDropdownProps> = ({ confirm, setSelectedKeys, clearFilters }) => (
		<Form layout='inline' style={{ padding: 8 }}>
			<Form.Item>
				<Input placeholder='Search' defaultValue={usernameInQuery} onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])} />
			</Form.Item>
			<Form.Item>
				<Button onClick={confirm}>Go</Button>
			</Form.Item>
			<Form.Item>
				<Button type='link' className={styles.actionButton} onClick={clearFilters}>
					Reset
				</Button>
			</Form.Item>
		</Form>
	)

	useEffect(() => {
		if (validLicenseInQuery) {
			getConsumers()
		}
	}, [validLicenseInQuery, getConsumers])

	return (
		<>
			<Modal
				width={700}
				destroyOnClose
				footer={null}
				centered={true}
				visible={validLicenseInQuery}
				onCancel={close}
				title={`Manage ${license.display_name} License`}
			>
				{!uploadData ? (
					<>
						<Row>
							<TotalLicenses initialTotal={license.quantity} onSave={updateTotal} isDisabled={!isAdmin && license.is_nfr}/>
						</Row>
						<Divider />
						<Row>
							<p className={styles.totalUnallocatedText}>({isLoading ? '-' : license.quantity - consumers.length} unallocated)</p>
						</Row>
						<Table rowKey='username' dataSource={consumers} loading={isLoading}>
							<Table.Column
								onFilter={filterByUsername}
								filterDropdown={props => <Filter {...props} />}
								defaultFilteredValue={usernameInQuery ? [usernameInQuery] : []}
								sorter={sortByUsername}
								sortDirections={['ascend', 'descend']}
								title='User'
								dataIndex='username'
							/>
							<Table.Column
								title='Actions'
								render={consumer => (
									<Popconfirm title='Are you sure you want to remove this user?' onConfirm={() => unassign(consumer.username)}>
										<Button type='link' className={styles.actionButton}>
											Remove
										</Button>
									</Popconfirm>
								)}
							/>
						</Table>
						<AssignUser create={assign} onChange={setAddValue} value={addValue} />
						<Divider />
						<FileSelector sendFileForValidation={validateFile} />
					</>
				) : (
					<>
						<ValidateUpload resetData={() => setUploadData(null)} data={uploadData} uploadDataHandler={uploadConsumerData} />
					</>
				)}
			</Modal>
			<Button type='link' className={styles.actionButton} icon='edit' onClick={(): void => history.push(`?type=${license.type}&serial=${license.serial}`)}>
				Manage
			</Button>
		</>
	)
}

export default EditLicenseModal
